import type { FolderRecord } from '../../data-binding/models/FolderRecord';
import {
  OrgAllocationService,
  type OrgAllocationAssignment,
  type OrgAllocationViewOptions,
} from './OrgAllocationService';

export interface OrgAllocationProjectionSnapshotOptions extends OrgAllocationViewOptions {
  readonly generatedAt?: Date | string;
  readonly maxPeople?: number;
  readonly maxTargets?: number;
}

export interface OrgAllocationProjectionSnapshot {
  readonly formatVersion: 'atlas-allocation-projection-summary.v1';
  readonly generatedAt: string;
  readonly asOf: string;
  readonly capacityTarget: number;
  readonly summary: {
    readonly assignmentCount: number;
    readonly peopleCount: number;
    readonly targetCount: number;
    readonly overAllocatedPeople: number;
    readonly underAllocatedPeople: number;
    readonly unknownPeople: number;
    readonly unspecifiedAssignments: number;
  };
  readonly targetKinds: readonly OrgAllocationProjectionCount[];
  readonly relationshipTypes: readonly OrgAllocationProjectionCount[];
  readonly sources: readonly OrgAllocationProjectionCount[];
  readonly people: readonly OrgAllocationProjectionPersonSummary[];
  readonly targets: readonly OrgAllocationProjectionTargetSummary[];
}

export interface OrgAllocationProjectionCount {
  readonly key: string;
  readonly count: number;
}

export interface OrgAllocationProjectionPersonSummary {
  readonly personExternalId: string;
  readonly personLabel: string;
  readonly totalAllocation: number;
  readonly assignmentCount: number;
  readonly hasUnspecifiedAllocation: boolean;
  readonly status: 'under_allocated' | 'fully_allocated' | 'over_allocated' | 'unknown';
}

export interface OrgAllocationProjectionTargetSummary {
  readonly targetExternalId: string;
  readonly targetLabel: string;
  readonly targetKind: string;
  readonly totalAllocation: number;
  readonly peopleCount: number;
  readonly assignmentCount: number;
  readonly hasUnspecifiedAllocation: boolean;
}

interface MutablePersonSummary {
  personExternalId: string;
  personLabel: string;
  totalAllocation: number;
  assignmentCount: number;
  hasUnspecifiedAllocation: boolean;
}

interface MutableTargetSummary {
  targetExternalId: string;
  targetLabel: string;
  targetKind: string;
  totalAllocation: number;
  people: Set<string>;
  assignmentCount: number;
  hasUnspecifiedAllocation: boolean;
}

/**
 * Materialized-style Atlas allocation projection builder.
 *
 * This is the bounded summary/drilldown path for large relationship sets. It
 * keeps only aggregate maps and top rows in memory instead of returning every
 * source-backed assignment to UI callers.
 */
export class OrgAllocationProjectionJobService {
  constructor(private readonly allocationService = new OrgAllocationService()) {}

  buildSnapshot(
    relationshipRecords: Iterable<FolderRecord>,
    options: OrgAllocationProjectionSnapshotOptions = {},
  ): OrgAllocationProjectionSnapshot {
    const asOf = toDate(options.asOf) ?? new Date();
    const capacityTarget = clampCapacityTarget(options.capacityTarget);
    const maxPeople = clampLimit(options.maxPeople ?? 25);
    const maxTargets = clampLimit(options.maxTargets ?? 25);
    const relationshipTypes = allowedSet(options.relationshipTypes);
    const targetKinds = allowedSet(options.targetKinds);
    const people = new Map<string, MutablePersonSummary>();
    const targets = new Map<string, MutableTargetSummary>();
    const targetKindCounts = new Map<string, number>();
    const relationshipTypeCounts = new Map<string, number>();
    const sourceCounts = new Map<string, number>();

    let assignmentCount = 0;
    let unspecifiedAssignments = 0;

    for (const record of relationshipRecords) {
      const relationshipType = stringProp(record.universalProps.relationshipType) ??
        stringProp(record.universalProps.predicate) ??
        'unknown';
      if (relationshipTypes.size > 0 && !relationshipTypes.has(relationshipType)) continue;
      const assignment = this.allocationService.relationshipRecordToAssignment(record);
      if (!assignment) continue;
      if (targetKinds.size > 0 && !targetKinds.has(assignment.targetKind)) continue;
      if (!isActiveAt(assignment, asOf)) continue;

      assignmentCount += 1;
      if (assignment.hasUnspecifiedAllocation) unspecifiedAssignments += 1;
      increment(relationshipTypeCounts, relationshipType);
      increment(targetKindCounts, assignment.targetKind);
      increment(sourceCounts, sourceLabel(assignment));
      applyPersonAssignment(people, assignment);
      applyTargetAssignment(targets, assignment);
    }

    const personRows = Array.from(people.values())
      .map((person) => finalizePerson(person, capacityTarget))
      .sort(comparePeopleForDrilldown)
      .slice(0, maxPeople);
    const targetRows = Array.from(targets.values())
      .map(finalizeTarget)
      .sort(compareTargetsForDrilldown)
      .slice(0, maxTargets);
    const allPeople = Array.from(people.values()).map((person) => finalizePerson(person, capacityTarget));

    return {
      formatVersion: 'atlas-allocation-projection-summary.v1',
      generatedAt: toIsoString(options.generatedAt) ?? new Date().toISOString(),
      asOf: asOf.toISOString(),
      capacityTarget,
      summary: {
        assignmentCount,
        peopleCount: people.size,
        targetCount: targets.size,
        overAllocatedPeople: allPeople.filter((person) => person.status === 'over_allocated').length,
        underAllocatedPeople: allPeople.filter((person) => person.status === 'under_allocated').length,
        unknownPeople: allPeople.filter((person) => person.status === 'unknown').length,
        unspecifiedAssignments,
      },
      targetKinds: countRows(targetKindCounts),
      relationshipTypes: countRows(relationshipTypeCounts),
      sources: countRows(sourceCounts),
      people: personRows,
      targets: targetRows,
    };
  }
}

function applyPersonAssignment(
  people: Map<string, MutablePersonSummary>,
  assignment: OrgAllocationAssignment,
): void {
  const existing = people.get(assignment.personExternalId) ?? {
    personExternalId: assignment.personExternalId,
    personLabel: assignment.personLabel,
    totalAllocation: 0,
    assignmentCount: 0,
    hasUnspecifiedAllocation: false,
  };
  existing.totalAllocation += assignment.allocation ?? 0;
  existing.assignmentCount += 1;
  existing.hasUnspecifiedAllocation = existing.hasUnspecifiedAllocation || assignment.hasUnspecifiedAllocation;
  people.set(existing.personExternalId, existing);
}

function applyTargetAssignment(
  targets: Map<string, MutableTargetSummary>,
  assignment: OrgAllocationAssignment,
): void {
  const existing = targets.get(assignment.targetExternalId) ?? {
    targetExternalId: assignment.targetExternalId,
    targetLabel: assignment.targetLabel,
    targetKind: assignment.targetKind,
    totalAllocation: 0,
    people: new Set<string>(),
    assignmentCount: 0,
    hasUnspecifiedAllocation: false,
  };
  existing.totalAllocation += assignment.allocation ?? 0;
  existing.people.add(assignment.personExternalId);
  existing.assignmentCount += 1;
  existing.hasUnspecifiedAllocation = existing.hasUnspecifiedAllocation || assignment.hasUnspecifiedAllocation;
  targets.set(existing.targetExternalId, existing);
}

function finalizePerson(
  person: MutablePersonSummary,
  capacityTarget: number,
): OrgAllocationProjectionPersonSummary {
  return {
    ...person,
    totalAllocation: roundAllocation(person.totalAllocation),
    status: loadStatus(person.totalAllocation, person.hasUnspecifiedAllocation, capacityTarget),
  };
}

function finalizeTarget(target: MutableTargetSummary): OrgAllocationProjectionTargetSummary {
  return {
    targetExternalId: target.targetExternalId,
    targetLabel: target.targetLabel,
    targetKind: target.targetKind,
    totalAllocation: roundAllocation(target.totalAllocation),
    peopleCount: target.people.size,
    assignmentCount: target.assignmentCount,
    hasUnspecifiedAllocation: target.hasUnspecifiedAllocation,
  };
}

function loadStatus(
  totalAllocation: number,
  hasUnspecifiedAllocation: boolean,
  capacityTarget: number,
): OrgAllocationProjectionPersonSummary['status'] {
  if (hasUnspecifiedAllocation && totalAllocation === 0) return 'unknown';
  if (totalAllocation > capacityTarget + 0.0001) return 'over_allocated';
  if (Math.abs(totalAllocation - capacityTarget) <= 0.0001) return 'fully_allocated';
  return 'under_allocated';
}

function comparePeopleForDrilldown(
  left: OrgAllocationProjectionPersonSummary,
  right: OrgAllocationProjectionPersonSummary,
): number {
  return statusRank(right.status) - statusRank(left.status) ||
    right.totalAllocation - left.totalAllocation ||
    right.assignmentCount - left.assignmentCount ||
    left.personLabel.localeCompare(right.personLabel);
}

function compareTargetsForDrilldown(
  left: OrgAllocationProjectionTargetSummary,
  right: OrgAllocationProjectionTargetSummary,
): number {
  return right.peopleCount - left.peopleCount ||
    right.totalAllocation - left.totalAllocation ||
    left.targetLabel.localeCompare(right.targetLabel);
}

function statusRank(status: OrgAllocationProjectionPersonSummary['status']): number {
  if (status === 'over_allocated') return 3;
  if (status === 'unknown') return 2;
  if (status === 'under_allocated') return 1;
  return 0;
}

function countRows(counts: ReadonlyMap<string, number>): readonly OrgAllocationProjectionCount[] {
  return Array.from(counts.entries())
    .map(([key, count]) => ({ key, count }))
    .sort((left, right) => right.count - left.count || left.key.localeCompare(right.key));
}

function increment(map: Map<string, number>, key: string): void {
  map.set(key, (map.get(key) ?? 0) + 1);
}

function sourceLabel(assignment: OrgAllocationAssignment): string {
  const label = assignment.source.label;
  return typeof label === 'string' && label.trim() ? label.trim() : 'unknown';
}

function isActiveAt(assignment: OrgAllocationAssignment, asOf: Date): boolean {
  const start = toDate(assignment.validFrom);
  const end = toDate(assignment.validTo);
  if (start && start.getTime() > asOf.getTime()) return false;
  if (end && end.getTime() < asOf.getTime()) return false;
  return true;
}

function toDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function toIsoString(value: Date | string | undefined): string | null {
  const date = toDate(value);
  return date ? date.toISOString() : null;
}

function clampCapacityTarget(value: number | undefined): number {
  if (!value || !Number.isFinite(value) || value <= 0) return 1;
  return Math.max(0.01, Math.min(10, value));
}

function clampLimit(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 25;
  return Math.max(1, Math.min(250, Math.floor(value)));
}

function allowedSet(values: readonly string[] | undefined): Set<string> {
  return new Set((values ?? []).map((value) => value.trim()).filter(Boolean));
}

function stringProp(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function roundAllocation(value: number): number {
  return Math.round(value * 10_000) / 10_000;
}
